import {
  addDateOnlyMonths,
  addDateOnlyYears,
  createCalendarGrid,
  daysInMonth,
  formatDateOnly,
  formatDateOnlyLabel,
  moveCalendarDate,
  parseDateOnly
} from "../core/date-navigation.js";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const FOCUSABLE_SELECTOR = [
  "button:not([disabled]):not([hidden])",
  "input:not([disabled]):not([hidden])",
  "[tabindex]:not([tabindex='-1']):not([hidden])"
].join(",");

export class GoToDateDialog {
  constructor({ element, onGo, onCancel = () => {} }) {
    if (!(element instanceof HTMLElement)) {
      throw new TypeError("Go To Date requires a dialog layer.");
    }
    if (typeof onGo !== "function") {
      throw new TypeError("Go To Date requires a navigation handler.");
    }

    this.element = element;
    this.onGo = onGo;
    this.onCancel = onCancel;
    this.dialog = requiredElement("[data-go-to-date-dialog]", element);
    this.backdrop = requiredElement("[data-go-to-date-backdrop]", element);
    this.closeButton = requiredElement("[data-go-to-date-close]", element);
    this.previousButton = requiredElement("[data-go-to-date-previous]", element);
    this.nextButton = requiredElement("[data-go-to-date-next]", element);
    this.monthButton = requiredElement("[data-go-to-date-month]", element);
    this.yearButton = requiredElement("[data-go-to-date-year]", element);
    this.picker = requiredElement("[data-go-to-date-picker]", element);
    this.pickerHeading = requiredElement("[data-go-to-date-picker-heading]", element);
    this.pickerPrevious = requiredElement("[data-go-to-date-picker-previous]", element);
    this.pickerNext = requiredElement("[data-go-to-date-picker-next]", element);
    this.pickerGrid = requiredElement("[data-go-to-date-picker-grid]", element);
    this.calendar = requiredElement("[data-go-to-date-calendar]", element);
    this.field = requiredElement("[data-go-to-date-field]", element);
    this.formatted = requiredElement("[data-go-to-date-formatted]", element);
    this.error = requiredElement("[data-go-to-date-error]", element);
    this.todayButton = requiredElement("[data-go-to-date-today]", element);
    this.cancelButton = requiredElement("[data-go-to-date-cancel]", element);
    this.goButton = requiredElement("[data-go-to-date-go]", element);
    this.goLabel = requiredElement("[data-go-to-date-go-label]", element);
    this.goSpinner = requiredElement("[data-go-to-date-go-spinner]", element);
    this.scopeThisInput = requiredElement("[data-go-to-date-scope-this]", element);
    this.scopeAllInput = requiredElement("[data-go-to-date-scope-all]", element);
    this.scopeCount = requiredElement("[data-go-to-date-scope-count]", element);
    this.scopeInputs = [this.scopeThisInput, this.scopeAllInput];

    this.selectedDate = null;
    this.focusedDate = null;
    this.displayDate = null;
    this.today = null;
    this.target = null;
    this.returnFocus = null;
    this.timeZone = "UTC";
    this.pickerMode = null;
    this.yearPageStart = 0;
    this.loading = false;
    this.chartCount = 1;

    this.closeButton.addEventListener("click", () => this.close());
    this.cancelButton.addEventListener("click", () => this.close());
    this.backdrop.addEventListener("click", () => this.close());
    this.previousButton.addEventListener("click", () => this.changeMonth(-1));
    this.nextButton.addEventListener("click", () => this.changeMonth(1));
    this.monthButton.addEventListener("click", () => this.togglePicker("month"));
    this.yearButton.addEventListener("click", () => this.togglePicker("year"));
    this.pickerPrevious.addEventListener("click", () => this.changePickerPage(-1));
    this.pickerNext.addEventListener("click", () => this.changePickerPage(1));
    this.pickerGrid.addEventListener("click", (event) => this.selectPickerValue(event));
    this.calendar.addEventListener("click", (event) => this.selectCalendarDate(event));
    this.calendar.addEventListener("keydown", (event) => this.handleCalendarKeydown(event));
    this.field.addEventListener("input", () => this.handleFieldInput(false));
    this.field.addEventListener("change", () => this.handleFieldInput(true));
    this.field.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      if (this.handleFieldInput(true)) this.submit();
    });
    this.todayButton.addEventListener("click", () => this.selectDate(this.today, { focus: true }));
    this.goButton.addEventListener("click", () => this.submit());
    this.dialog.addEventListener("keydown", (event) => this.handleDialogKeydown(event));
  }

  get open() {
    return !this.element.hidden;
  }

  show({ initialDate, today, target, returnFocus, timeZone = "UTC", chartCount = 1 }) {
    const selected = parseDateOnly(formatDateOnly(initialDate))
      || parseDateOnly(formatDateOnly(today));
    if (!selected) throw new TypeError("Go To Date requires a valid initial date.");

    this.target = target;
    this.returnFocus = returnFocus instanceof HTMLElement ? returnFocus : document.activeElement;
    this.timeZone = timeZone;
    this.today = parseDateOnly(formatDateOnly(today)) || selected;
    this.selectedDate = selected;
    this.focusedDate = selected;
    this.displayDate = { ...selected };
    this.pickerMode = null;
    this.yearPageStart = yearPageStart(selected.year);
    this.chartCount = Math.max(1, Number.parseInt(chartCount, 10) || 1);
    this.scopeThisInput.checked = true;
    this.scopeAllInput.checked = false;
    this.scopeCount.textContent = `(${this.chartCount})`;
    this.setLoading(false);
    this.clearError();
    this.closePicker();
    this.element.hidden = false;
    this.syncField();
    this.renderCalendar({ focus: true });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.open) return false;
    if (this.loading) {
      this.onCancel({
        target: this.target,
        scope: this.scopeAllInput.checked ? "all" : "this"
      });
      this.setLoading(false);
    }
    this.element.hidden = true;
    this.closePicker();
    const focusTarget = this.returnFocus;
    this.target = null;
    this.returnFocus = null;
    if (restoreFocus && focusTarget instanceof HTMLElement && focusTarget.isConnected) {
      focusTarget.focus({ preventScroll: true });
    }
    return true;
  }

  closeForTarget(target, { restoreFocus = false } = {}) {
    if (this.target !== target) return false;
    return this.close({ restoreFocus });
  }

  changeMonth(amount) {
    if (this.loading) return;
    this.focusedDate = addDateOnlyMonths(this.focusedDate, amount);
    this.displayDate = { ...this.focusedDate };
    this.closePicker();
    this.renderCalendar({ focus: true });
  }

  togglePicker(mode) {
    if (this.loading) return;
    if (this.pickerMode === mode && !this.picker.hidden) {
      this.closePicker();
      return;
    }
    this.pickerMode = mode;
    this.monthButton.setAttribute("aria-expanded", String(mode === "month"));
    this.yearButton.setAttribute("aria-expanded", String(mode === "year"));
    this.yearPageStart = yearPageStart(this.displayDate.year);
    this.picker.hidden = false;
    this.renderPicker();
    const focusTarget = this.pickerGrid.querySelector("button[aria-current='true']")
      || this.pickerGrid.querySelector("button");
    focusTarget?.focus();
  }

  closePicker() {
    this.picker.hidden = true;
    this.pickerMode = null;
    this.monthButton.setAttribute("aria-expanded", "false");
    this.yearButton.setAttribute("aria-expanded", "false");
  }

  changePickerPage(amount) {
    if (this.pickerMode === "year") {
      this.yearPageStart = Math.max(1, Math.min(9988, this.yearPageStart + amount * 12));
    } else {
      this.displayDate = addDateOnlyYears(this.displayDate, amount);
      this.focusedDate = {
        year: this.displayDate.year,
        month: this.focusedDate.month,
        day: Math.min(
          this.focusedDate.day,
          daysInMonth(this.displayDate.year, this.focusedDate.month)
        )
      };
    }
    this.renderPicker();
  }

  renderPicker() {
    this.pickerGrid.replaceChildren();
    if (this.pickerMode === "month") {
      this.pickerHeading.textContent = String(this.displayDate.year);
      this.pickerPrevious.setAttribute("aria-label", "Previous year");
      this.pickerNext.setAttribute("aria-label", "Next year");
      MONTH_NAMES.forEach((name, index) => {
        const button = pickerButton(name.slice(0, 3), "month", String(index + 1));
        button.title = name;
        button.setAttribute("aria-current", String(index + 1 === this.displayDate.month));
        button.setAttribute("aria-selected", String(index + 1 === this.displayDate.month));
        this.pickerGrid.append(button);
      });
      return;
    }

    const pageEnd = Math.min(9999, this.yearPageStart + 11);
    this.pickerHeading.textContent = `${this.yearPageStart}–${pageEnd}`;
    this.pickerPrevious.setAttribute("aria-label", "Previous 12 years");
    this.pickerNext.setAttribute("aria-label", "Next 12 years");
    for (let year = this.yearPageStart; year <= pageEnd; year += 1) {
      const button = pickerButton(String(year), "year", String(year));
      button.setAttribute("aria-current", String(year === this.displayDate.year));
      button.setAttribute("aria-selected", String(year === this.displayDate.year));
      this.pickerGrid.append(button);
    }
  }

  selectPickerValue(event) {
    const button = event.target instanceof Element
      ? event.target.closest("button[data-calendar-picker-value]")
      : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const value = Number.parseInt(button.dataset.calendarPickerValue, 10);
    if (button.dataset.calendarPickerKind === "month") {
      this.displayDate = { ...this.displayDate, month: value };
    } else {
      this.displayDate = { ...this.displayDate, year: value };
    }
    this.focusedDate = {
      year: this.displayDate.year,
      month: this.displayDate.month,
      day: Math.min(
        this.focusedDate.day,
        daysInMonth(this.displayDate.year, this.displayDate.month)
      )
    };
    this.closePicker();
    this.renderCalendar({ focus: true });
  }

  renderCalendar({ focus = false } = {}) {
    this.monthButton.textContent = MONTH_NAMES[this.displayDate.month - 1];
    this.monthButton.setAttribute("aria-label", `Choose month, ${this.monthButton.textContent}`);
    this.yearButton.textContent = String(this.displayDate.year);
    this.yearButton.setAttribute("aria-label", `Choose year, ${this.displayDate.year}`);
    const focusedValue = formatDateOnly(this.focusedDate);
    const cells = createCalendarGrid(this.displayDate, this.selectedDate, this.today);
    this.calendar.replaceChildren(...cells.map((cell) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "go-to-date-day";
      button.dataset.date = cell.value;
      button.textContent = String(cell.date.day);
      button.tabIndex = cell.value === focusedValue ? 0 : -1;
      button.classList.toggle("is-overflow", !cell.inMonth);
      button.classList.toggle("is-selected", cell.selected);
      button.classList.toggle("is-today", cell.today);
      button.setAttribute("aria-label", formatDateOnlyLabel(cell.date));
      button.setAttribute("aria-selected", String(cell.selected));
      if (cell.today) button.setAttribute("aria-current", "date");
      return button;
    }));
    if (focus) {
      requestAnimationFrame(() => this.calendar.querySelector(
        `[data-date="${focusedValue}"]`
      )?.focus({ preventScroll: true }));
    }
  }

  selectCalendarDate(event) {
    const button = event.target instanceof Element
      ? event.target.closest("button[data-date]")
      : null;
    if (!(button instanceof HTMLButtonElement) || this.loading) return;
    const date = parseDateOnly(button.dataset.date);
    if (date) this.selectDate(date, { focus: true });
  }

  selectDate(date, { focus = false } = {}) {
    const selected = parseDateOnly(formatDateOnly(date));
    if (!selected) return false;
    this.selectedDate = selected;
    this.focusedDate = selected;
    this.displayDate = { ...selected };
    this.closePicker();
    this.clearError();
    this.syncField();
    this.renderCalendar({ focus });
    return true;
  }

  handleCalendarKeydown(event) {
    if (!(event.target instanceof HTMLButtonElement) || this.loading) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const date = parseDateOnly(event.target.dataset.date);
      if (!date) return;
      if (formatDateOnly(date) === formatDateOnly(this.selectedDate)) this.submit();
      else this.selectDate(date, { focus: true });
      return;
    }
    const moved = moveCalendarDate(this.focusedDate, event.key, event);
    if (!moved) return;
    event.preventDefault();
    this.focusedDate = moved;
    this.displayDate = { ...moved };
    this.closePicker();
    this.renderCalendar({ focus: true });
  }

  handleFieldInput(commit) {
    const parsed = parseDateOnly(this.field.value);
    if (!parsed) {
      if (commit || this.field.value.trim().length >= 10) {
        this.showError("Enter a valid date in YYYY-MM-DD format.");
      } else {
        this.clearError();
      }
      this.field.setAttribute("aria-invalid", "true");
      return false;
    }
    this.field.setAttribute("aria-invalid", "false");
    this.selectedDate = parsed;
    this.focusedDate = parsed;
    this.displayDate = { ...parsed };
    this.closePicker();
    this.clearError();
    this.syncField();
    this.renderCalendar();
    return true;
  }

  syncField() {
    this.field.value = formatDateOnly(this.selectedDate);
    this.field.setAttribute("aria-invalid", "false");
    this.formatted.textContent = formatDateOnlyLabel(this.selectedDate);
  }

  async submit() {
    if (this.loading || !this.handleFieldInput(true)) return false;
    this.setLoading(true);
    this.clearError();
    try {
      await this.onGo({
        date: formatDateOnly(this.selectedDate),
        target: this.target,
        timeZone: this.timeZone,
        scope: this.scopeAllInput.checked ? "all" : "this"
      });
      this.setLoading(false);
      this.close();
      return true;
    } catch (error) {
      this.setLoading(false);
      if (!this.open) return false;
      if (error?.name === "AbortError") {
        this.showError("Navigation was interrupted. Try again.");
      } else {
        this.showError(error?.message || "That date could not be loaded.");
      }
      return false;
    }
  }

  setLoading(loading) {
    this.loading = Boolean(loading);
    this.element.toggleAttribute("data-loading", this.loading);
    this.goButton.disabled = this.loading;
    this.todayButton.disabled = this.loading;
    this.cancelButton.disabled = this.loading;
    this.closeButton.disabled = this.loading;
    this.previousButton.disabled = this.loading;
    this.nextButton.disabled = this.loading;
    this.monthButton.disabled = this.loading;
    this.yearButton.disabled = this.loading;
    this.scopeThisInput.disabled = this.loading;
    this.scopeAllInput.disabled = this.loading || this.chartCount <= 1;
    this.field.readOnly = this.loading;
    this.goLabel.textContent = this.loading ? "Loading" : "Go";
    this.goSpinner.hidden = !this.loading;
  }

  showError(message) {
    this.error.textContent = String(message || "");
    this.error.hidden = false;
  }

  clearError() {
    this.error.textContent = "";
    this.error.hidden = true;
  }

  handleDialogKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter((candidate) => candidate instanceof HTMLElement && candidate.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

function pickerButton(label, kind, value) {
  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("role", "option");
  button.textContent = label;
  button.dataset.calendarPickerKind = kind;
  button.dataset.calendarPickerValue = value;
  return button;
}

function yearPageStart(year) {
  return Math.max(1, Math.min(9988, Math.floor((year - 1) / 12) * 12 + 1));
}

function requiredElement(selector, root) {
  const element = root.querySelector(selector);
  if (!(element instanceof HTMLElement)) throw new Error(`Required element not found: ${selector}`);
  return element;
}
